import moment from 'moment';

import { isCurrentAiProcessInProgress } from '../../../../utils/helpers/aiOptimizerHelpers';
import { addNewVersionToArray } from '../../../../utils/helpers/contentOptimizerVersionsHelper';

import { editorTypes } from '../constants/tiptapEditorConstants';

import { tiptapEditorActions } from './tiptapEditor.actions';

const fullWidthOptions = {
    outline: 'outlineFullWidth',
    optimizer: 'editorFullWidth',
};

const getTargetState = (state, editorType) => state?.[editorType] || state?.[editorTypes.OPTIMIZER];
const parseBooleanFromLocalStorage = (str) => str === 'true';

const contentOptimizerEditorInitialState = {
    [editorTypes.OPTIMIZER]: {
        // Main editor content
        currentEditorContent: null,
        currentEditorText: null,
        currentEditorId: null,
        additionalEditorContent: null,

        // Loadings
        currentEditorLoading: false,
        saveEditorLoading: false,
        previewVersionLoading: false,

        // Versions
        editorVersions: null,
        currentEditorVersion: null,
        currentVersionImportedUrl: null,
        previewVersion: null,

        // Settings
        fullWidth: parseBooleanFromLocalStorage(localStorage.getItem(fullWidthOptions.optimizer)),
    },
    [editorTypes.OUTLINE]: {
        // Main editor content
        currentEditorContent: null,
        currentEditorText: null,
        currentEditorId: null,
        additionalEditorContent: null,
        editorNewTitleContent: null,

        // Loadings
        currentEditorLoading: false,
        saveEditorLoading: false,
        previewVersionLoading: false,

        // Versions
        editorVersions: null,
        currentEditorVersion: null,
        previewVersion: null,

        // Settings
        fullWidth: parseBooleanFromLocalStorage(localStorage.getItem(fullWidthOptions.outline)),
    },

    // BasicData contains rs score, readability, word count
    basicData: {
        readability: null,
        rankability_score: null,
        word_count: 0,
        optimal_readability: {
            min: 100,
            max: 100,
        },
        optimal_rankability_score: {
            min: 0,
            max: 85,
        },
        optimal_word_count: 0,
    },

    // Settings
    highlightKeywords: true,
    htmlTags: JSON.parse(localStorage.getItem('editorHtmlTags')) || false,
    tableOfContents: JSON.parse(localStorage.getItem('editorTableOfContents')),
    tableOfContentsItems: [],

    // AI
    generatedAIToken: null,
    aiState: {
        isLoading: false,
        errorMessage: null,
        response: null,
        optimizerId: null,
    },
    aiSpellCheckerVisible: false,
};

export const contentOptimizerEditorReducer = (
    state = contentOptimizerEditorInitialState,
    { type, payload }
) => {
    switch (type) {
        // Main editor content
        case tiptapEditorActions.UPDATE_CURRENT_EDITOR_CONTENT: {
            const { editorType, content } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    currentEditorContent: content,
                    currentEditorVersion: {
                        ...targetTypeEditorState.currentEditorVersion,
                        [editorType === editorTypes.OPTIMIZER ? 'content' : 'outline']:
                            JSON.stringify(content),
                    },
                },
            };
        }
        case tiptapEditorActions.ADDITIONAL_CONTENT_TO_EDITOR: {
            const { editorType, content } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    additionalEditorContent: content,
                },
            };
        }
        case tiptapEditorActions.UPDATE_CURRENT_EDITOR_TEXT: {
            const { editorType, text } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    currentEditorText: text,
                },
            };
        }
        case tiptapEditorActions.NEW_TITLE_CONTENT_TO_OUTLINE_EDITOR: {
            const targetTypeEditorState = getTargetState(state, editorTypes.OUTLINE);

            return {
                ...state,
                [editorTypes.OUTLINE]: {
                    ...targetTypeEditorState,
                    editorNewTitleContent: payload,
                },
            };
        }

        // Loadings
        case tiptapEditorActions.UPDATE_SAVE_EDITOR_LOADING: {
            const { editorType, loading } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    saveEditorLoading: loading,
                },
            };
        }
        case tiptapEditorActions.UPDATE_CURRENT_EDITOR_LOADING: {
            const { editorType, loading } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    currentEditorLoading: loading,
                },
            };
        }
        case tiptapEditorActions.UPDATE_PREVIEW_VERSION_CONTENT_LOADING: {
            const { editorType, loading } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    previewVersionLoading: loading,
                },
            };
        }

        // Versions
        case tiptapEditorActions.UPDATE_EDITOR_VERSIONS: {
            const { editorType, versions } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);
            const sortedVersions = versions.sort(
                (a, b) => moment(b.created_at) - moment(a.created_at)
            );

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: sortedVersions,
                    previewVersion: null,
                },
            };
        }
        case tiptapEditorActions.UPDATE_EDITOR_VERSIONS_SCORES: {
            const { editorType, scores } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);
            const scoreMap = Object.fromEntries(
                scores.map((item) => [item.id, item.rankability_score])
            );

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: targetTypeEditorState.editorVersions.map((item) => ({
                        ...item,
                        rankability_score: scoreMap[item.id] ?? item.rankability_score,
                    })),
                },
            };
        }
        case tiptapEditorActions.ADD_EDITOR_VERSION: {
            const { editorType, version } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: addNewVersionToArray(
                        version,
                        targetTypeEditorState.editorVersions
                    ),
                },
            };
        }
        case tiptapEditorActions.UPDATE_CURRENT_EDITOR_VERSION: {
            const { editorType, version } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);
            const currentVersionImportedUrl =
                editorType === editorTypes.OPTIMIZER && version?.import_url
                    ? version.import_url
                    : null;

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    currentEditorVersion: version,
                    currentEditorId: version.id,
                    currentVersionImportedUrl,
                },
            };
        }
        case tiptapEditorActions.UPDATE_EDITOR_CURRENT_VERSION_SCORE: {
            const { editorType, score } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: targetTypeEditorState.editorVersions?.map((item) =>
                        item?.active ? { ...item, rankability_score: score } : item
                    ),
                    currentEditorVersion: {
                        ...targetTypeEditorState.currentEditorVersion,
                        rankability_score: score,
                    },
                },
                basicData: {
                    ...state.basicData,
                    rankability_score: score,
                },
            };
        }
        case tiptapEditorActions.UPDATE_EDITOR_CURRENT_VERSION_WORD_COUNT: {
            const { editorType, wordCount } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: targetTypeEditorState.editorVersions?.map((item) =>
                        item?.active ? { ...item, word_count: wordCount } : item
                    ),
                    currentEditorVersion: {
                        ...targetTypeEditorState.currentEditorVersion,
                        word_count: wordCount,
                    },
                },
            };
        }
        case tiptapEditorActions.UPDATE_EDITOR_CURRENT_VERSION_HEADING_COUNT: {
            const { editorType, headingCount } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: targetTypeEditorState.editorVersions?.map((item) =>
                        item?.active ? { ...item, heading_count: headingCount } : item
                    ),
                    currentEditorVersion: {
                        ...targetTypeEditorState.currentEditorVersion,
                        heading_count: headingCount,
                    },
                },
            };
        }
        case tiptapEditorActions.UPDATE_OPTIMIZER_EDITOR_VERSION_IMPORTED_URL: {
            const targetTypeEditorState = getTargetState(state, editorTypes.OPTIMIZER);

            return {
                ...state,
                [editorTypes.OPTIMIZER]: {
                    ...targetTypeEditorState,
                    currentVersionImportedUrl: payload,
                },
            };
        }
        case tiptapEditorActions.SET_EDITOR_PREVIEW_VERSION: {
            const { editorType, versionId, updateBasicData, versionContent } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);
            const foundVersion = targetTypeEditorState.editorVersions?.find(
                (item) => item.id === versionId
            );
            const previewVersion = foundVersion
                ? {
                      ...foundVersion,
                      content: versionContent,
                  }
                : null;

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    previewVersion,
                },
                basicData: updateBasicData
                    ? {
                          ...state.basicData,
                          rankability_score:
                              previewVersion?.rankability_score ??
                              previewVersion?.progress ??
                              targetTypeEditorState.currentEditorVersion.rankability_score,
                          word_count:
                              previewVersion?.word_count ??
                              targetTypeEditorState.currentEditorVersion.word_count,
                          readability:
                              previewVersion?.readability ??
                              targetTypeEditorState.currentEditorVersion.readability,
                      }
                    : { ...state.basicData },
            };
        }
        case tiptapEditorActions.UPDATE_EDITOR_VERSION_NAME: {
            const { editorType, id, name } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: targetTypeEditorState.editorVersions?.map((item) =>
                        item.id === id ? { ...item, name } : item
                    ),
                },
            };
        }
        case tiptapEditorActions.DELETE_EDITOR_VERSION: {
            const { editorType, id } = payload;
            const targetTypeEditorState = getTargetState(state, editorType);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    editorVersions: targetTypeEditorState.editorVersions?.filter(
                        (item) => item.id !== id
                    ),
                },
            };
        }

        // BasicData contains rs score, readability, word count
        case tiptapEditorActions.UPDATE_EDITOR_BASIC_DATA: {
            return {
                ...state,
                basicData: {
                    ...state.basicData,
                    ...payload,
                },
            };
        }

        // Settings
        case tiptapEditorActions.UPDATE_HIGHLIGHT_KEYWORDS: {
            return {
                ...state,
                highlightKeywords: payload,
            };
        }
        case tiptapEditorActions.UPDATE_EDITOR_FULL_WIDTH: {
            const { editorType, status } = payload;
            const widthStorageItemKey =
                editorType === editorTypes.OPTIMIZER
                    ? fullWidthOptions.optimizer
                    : fullWidthOptions.outline;
            const targetTypeEditorState = getTargetState(state, editorType);

            localStorage.setItem(widthStorageItemKey, status);

            return {
                ...state,
                [editorType]: {
                    ...targetTypeEditorState,
                    fullWidth: status,
                },
            };
        }
        case tiptapEditorActions.UPDATE_HTML_TAGS: {
            localStorage.setItem('editorHtmlTags', payload || false);

            return {
                ...state,
                htmlTags: payload || false,
            };
        }
        case tiptapEditorActions.UPDATE_TABLE_OF_CONTENTS: {
            localStorage.setItem('editorTableOfContents', payload || false);

            return {
                ...state,
                tableOfContents: payload || false,
            };
        }
        case tiptapEditorActions.SET_TABLE_OF_CONTENT_ITEMS: {
            if (JSON.stringify(state.tableOfContentsItems) === JSON.stringify(payload)) {
                return state;
            }

            return {
                ...state,
                tableOfContentsItems: payload,
            };
        }

        // AI
        case tiptapEditorActions.SET_GENERATED_AI_TOKEN: {
            return {
                ...state,
                generatedAIToken: payload,
            };
        }
        case tiptapEditorActions.SET_AI_STATE: {
            return {
                ...state,
                aiState: {
                    ...state.aiState,
                    ...payload,
                },
            };
        }
        case tiptapEditorActions.SET_AI_SPELL_CHECKER_VISIBLE: {
            return {
                ...state,
                aiSpellCheckerVisible: payload,
            };
        }
        case tiptapEditorActions.RESET_AI_STATE: {
            return {
                ...state,
                aiState: contentOptimizerEditorInitialState.aiState,
                aiSpellCheckerVisible: false,
            };
        }
        case tiptapEditorActions.SET_AI_OPTIMIZED_ID: {
            return {
                ...state,
                aiState: {
                    ...state.aiState,
                    optimizerId: payload,
                },
            };
        }

        case tiptapEditorActions.CLEAR_TIPTAP_EDITOR: {
            const tableOfContents = state.tableOfContents;
            const htmlTags = state.htmlTags;

            // According to the requirement: make the only Outline editor's width to be a regular one by default
            localStorage.setItem(fullWidthOptions.outline, false);

            return {
                ...contentOptimizerEditorInitialState,
                tableOfContents,
                htmlTags,
            };
        }

        case tiptapEditorActions.CLEAR_EDITOR_BY_TYPE: {
            const { fullWidth, currentEditorVersion } = state[payload];

            const isAiOutlineInProgress =
                payload === editorTypes.OUTLINE &&
                !!currentEditorVersion &&
                isCurrentAiProcessInProgress(currentEditorVersion.generation_status);

            return {
                ...state,
                [payload]: {
                    ...contentOptimizerEditorInitialState[payload],
                    /*
                     * Don't clear the current editor version when clearing the outline editor
                     * in order to proceed updating outline progress if there is outline version in progress.
                     */
                    ...(isAiOutlineInProgress
                        ? {
                              currentEditorVersion: currentEditorVersion,
                              currentEditorContent: state[payload].currentEditorContent,
                          }
                        : {}),
                    fullWidth,
                },
            };
        }

        default:
            return state;
    }
};
